export type MenuItem = {
    name: string
    description: string
    price: string
    vegetarian?: boolean
}

export type MenuCategory = {
    slug: string
    label: string
    subtitle: string
    intro: string
    items: Array<MenuItem>
}

export const DEFAULT_MENU_TAB = 'mezze'

export const MENU: Array<MenuCategory> = [
    {
        slug: 'mezze',
        label: "Mezze",
        subtitle: "To begin, to share",
        intro: "Small plates for the middle of the table. Order several, order early.",
        items: [
            { name: "Hummus, brown butter", description: "Warm chickpeas, burnt butter, Aleppo pepper, olive oil.", price: '11', vegetarian: true },
            { name: "Muhammara", description: "Roasted red pepper, walnut, pomegranate molasses, cumin.", price: '10', vegetarian: true },
            { name: "Smoked aubergine, pomegranate", description: "Coal-blistered aubergine, tahini, mint, pomegranate seeds.", price: '12', vegetarian: true },
            { name: "Labneh, za’atar oil", description: "Strained yogurt, wild thyme, sumac, warm flatbread.", price: '10', vegetarian: true },
            { name: "Beef tartare, sumac", description: "Hand-cut beef, cured yolk, shallot, bulgur crisp.", price: '16' },
            { name: "Fattoush", description: "Little gem, radish, purslane, toasted pita, sumac dressing.", price: '12', vegetarian: true },
            { name: "Sea bream crudo", description: "Blood orange, green chili, olive oil, sea salt.", price: '15' },
            { name: "Grilled halloumi, fig", description: "Charred halloumi, roasted fig, thyme honey.", price: '13', vegetarian: true }
        ]
    },
    {
        slug: 'feu',
        label: "Feu",
        subtitle: "From the fire",
        intro: "Charcoal for heat, oak for scent. Everything here is cooked over live fire.",
        items: [
            { name: "Charcoal-lacquered lamb rack", description: "Pomegranate jus, freekeh, smoked shallot.", price: '42' },
            { name: "Whole grilled sea bream", description: "Verjus beurre blanc, wild herbs, charred lemon.", price: '38' },
            { name: "Chicken supreme", description: "Smoked yogurt, charred tomato, crisp skin.", price: '29' },
            { name: "Wood-fired cauliflower", description: "Tahini, burnt honey, pistachio dukkah.", price: '22', vegetarian: true },
            { name: "Hand-minced beef kofta", description: "Pickled chili, sumac onion, warm pide.", price: '26' },
            { name: "Duck breast, fig", description: "Fig glaze, bitter leaves, pomegranate jus.", price: '36' },
            { name: "King prawns, Aleppo butter", description: "Grilled in the shell with garlic, parsley and lemon.", price: '34' },
            { name: "Shakshuka", description: "Eggs baked in smoked tomato and pepper, feta, coriander.", price: '18', vegetarian: true }
        ]
    },
    {
        slug: 'pain',
        label: "Pain",
        subtitle: "Bread",
        intro: "Out of the oak oven at the top of every hour. Tear it, share it, ask for more.",
        items: [
            { name: "Hot pide, brown butter", description: "Straight from the oven, nigella seed, sea salt.", price: '6', vegetarian: true },
            { name: "Lamb pide", description: "Spiced minced lamb, sweet pepper, parsley, lemon.", price: '16' },
            { name: "Za’atar man’oushe", description: "Wild thyme, sesame, sumac, olive oil.", price: '8', vegetarian: true },
            { name: "Sucuk and egg pide", description: "Spiced beef sausage, egg yolk, chili butter.", price: '15' },
            { name: "Three-cheese pide", description: "Kashkaval, feta and mozzarella, black pepper.", price: '13', vegetarian: true },
            { name: "Lavash, whipped feta", description: "Blistered over the coals, with whipped feta and honey.", price: '8', vegetarian: true },
            { name: "Levain, cultured butter", description: "Our sourdough with a slab of salted cultured butter.", price: '6', vegetarian: true }
        ]
    },
    {
        slug: 'douceurs',
        label: "Douceurs",
        subtitle: "Sweet",
        intro: "Orange blossom, pistachio, burnt sugar. The quiet end of the evening.",
        items: [
            { name: "Kunefe", description: "Shredded pastry, warm cheese, pistachio, orange blossom syrup.", price: '13', vegetarian: true },
            { name: "Baklava, pistachio", description: "Thirty-two layers, Antep pistachio, clarified butter.", price: '11', vegetarian: true },
            { name: "Rice pudding, orange blossom", description: "Slow-cooked milk rice, orange blossom, burnt sugar.", price: '9', vegetarian: true },
            { name: "Crème brûlée, cardamom", description: "Green cardamom custard under a glass-thin crust.", price: '11', vegetarian: true },
            { name: "Chocolate, olive oil, sea salt", description: "Dark chocolate mousse, grassy olive oil, smoked salt.", price: '12', vegetarian: true },
            { name: "Fig tart", description: "Almond frangipane, roasted figs, labneh cream.", price: '12', vegetarian: true },
            { name: "Malabi", description: "Milk pudding, rose syrup, crushed pistachio.", price: '9', vegetarian: true },
            { name: "Dates and lokum", description: "Medjool dates and house Turkish delight, for the coffee.", price: '7', vegetarian: true }
        ]
    },
    {
        slug: 'sans-alcool',
        label: "Sans Alcool",
        subtitle: "The zero-proof bar",
        intro: "Composed with the same care as any cocktail list. Each one is written for a dish.",
        items: [
            { name: "Sumac & Pomegranate Sour", description: "Pomegranate, sumac, lemon, a soft aquafaba foam.", price: '12' },
            { name: "Smoked Tea Tonic", description: "Lapsang souchong, house tonic, grapefruit peel.", price: '11' },
            { name: "Cardamom Coffee Soda", description: "Cold brew, green cardamom, orange oil, soda.", price: '10' },
            { name: "Rose & Cucumber Cooler", description: "Pressed cucumber, rose water, lime, mint.", price: '11' },
            { name: "Fig Leaf & Ginger Fizz", description: "Fig leaf cordial, fresh ginger, verjus, soda.", price: '12' },
            { name: "Salted Ayran", description: "Whipped yogurt, sea salt, dried mint.", price: '7' },
            { name: "Turkish coffee", description: "Cooked slowly in a copper cezve, with a piece of lokum.", price: '5' },
            { name: "Cardamom coffee", description: "Light-roast Arabic coffee, green cardamom, saffron.", price: '5' },
            { name: "Tea", description: "Fresh mint, sage and black tea, or chamomile and orange blossom.", price: '4' }
        ]
    }
]

export const menuHref = (slug: string): string => (slug === DEFAULT_MENU_TAB ? '/menu' : `/menu/${slug}`)
